import classNames from 'classnames'
import { DatePicker, TimePicker } from 'antd'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { DraggableProvidedDragHandleProps } from 'react-beautiful-dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faChevronDown,
  faChevronUp,
  faCircleInfo,
  faGripVertical,
  faPen,
  faTrashAlt,
} from '@fortawesome/pro-solid-svg-icons'
import TextInput from 'components/ui/forms/field/text-input/TextInput'
import RequiredFieldLabel from 'components/reusables/requiredFieldLabel'
import { ISessionItemModel } from 'models/block.model'
import {
  formatSessionDate,
  formatSessionTime,
  parseSessionDate,
  parseSessionTime,
} from 'helpers/sessionBlock.helpers'
import styles from './Session.module.scss'

export const SESSION_DESCRIPTION_MAX_LENGTH = 200

const translationPrefix = 'administration.blocks.type.sessions'

const FieldHint = ({ children }: { children: React.ReactNode }) => (
  <p className={styles.fieldHint}>
    <FontAwesomeIcon icon={faCircleInfo} className={styles.fieldHintIcon} />
    <span>{children}</span>
  </p>
)

interface SessionCardProps {
  session: ISessionItemModel
  index: number
  isExpanded: boolean
  isEditingTitle: boolean
  canRemove: boolean
  dragHandleProps?: DraggableProvidedDragHandleProps | null
  onToggleExpand: () => void
  onToggleEditTitle: () => void
  onUpdate: (patch: Partial<ISessionItemModel>) => void
  onRemove: () => void
}

export const SessionCard = ({
  session,
  index,
  isExpanded,
  isEditingTitle,
  canRemove,
  dragHandleProps,
  onToggleExpand,
  onToggleEditTitle,
  onUpdate,
  onRemove,
}: SessionCardProps) => {
  const { t } = useTranslation()
  const descriptionLength = (session.description || '').length
  const sessionTitle = session.name.trim()
    ? session.name
    : t(`${translationPrefix}.sessionLabel`, { number: index + 1 })
  const sessionDate = parseSessionDate(session.startDate)

  return (
    <div
      className={styles.sessionCard}
      data-test-id={`panel-session-item-${index}`}
    >
      <div className={styles.sessionCardHeader}>
        <div className={styles.sessionCardHeaderLeft}>
          <button
            type="button"
            className={styles.dragHandle}
            {...dragHandleProps}
            data-test-id={`session-drag-handle-${index}`}
          >
            <FontAwesomeIcon
              icon={faGripVertical}
              className={styles.dragHandleIcon}
            />
          </button>

          {isEditingTitle ? (
            <TextInput
              name={`session-title-${index}`}
              value={session.name}
              label=""
              placeHolder={t(`${translationPrefix}.placeholders.writeHere`)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onUpdate({ name: e.target.value })
              }
              onBlur={onToggleEditTitle}
              error=""
              className={styles.titleInput}
            />
          ) : (
            <span className={styles.sessionCardTitle}>{sessionTitle}</span>
          )}

          <button
            type="button"
            className={styles.iconButton}
            onClick={onToggleEditTitle}
            data-test-id={`button-edit-session-title-${index}`}
          >
            <FontAwesomeIcon icon={faPen} />
          </button>
        </div>

        <div className={styles.sessionCardHeaderActions}>
          {canRemove && (
            <button
              type="button"
              className={styles.deleteButton}
              onClick={onRemove}
              data-test-id={`button-remove-session-${index}`}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
          )}
          <button
            type="button"
            className={styles.iconButton}
            onClick={onToggleExpand}
            data-test-id={`button-toggle-session-${index}`}
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={styles.sessionCardBody}>
          <div className={styles.fieldGroup}>
            <RequiredFieldLabel className={styles.sessionFieldLabel}>
              {t(`${translationPrefix}.fields.sessionDescription`)}
            </RequiredFieldLabel>
            <TextInput
              name={`session-description-${index}`}
              value={session.description ?? ''}
              label=""
              placeHolder={t(`${translationPrefix}.placeholders.writeHere`)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onUpdate({
                  description: e.target.value.slice(
                    0,
                    SESSION_DESCRIPTION_MAX_LENGTH
                  ),
                })
              }
              error=""
              classes={{ input: styles.sessionInput }}
            />
            <FieldHint>
              {`${descriptionLength}/${SESSION_DESCRIPTION_MAX_LENGTH} ${t(
                `${translationPrefix}.fields.charCounterLabel`
              )}`}
            </FieldHint>
          </div>

          <div className={styles.dateTimeRow}>
            <div className={styles.dateTimeField}>
              <RequiredFieldLabel required className={styles.sessionFieldLabel}>
                {t(`${translationPrefix}.fields.startDate`)}
              </RequiredFieldLabel>
              <DatePicker
                format="DD/MMM/YYYY"
                className={classNames('w-100', styles.datePicker)}
                dropdownClassName={styles.datePickerDropdown}
                value={sessionDate ? moment(sessionDate) : undefined}
                disabledDate={(current) =>
                  !!current && current.isBefore(moment().startOf('day'), 'day')
                }
                onChange={(date) =>
                  onUpdate({
                    startDate: formatSessionDate(date ? date.toDate() : null),
                  })
                }
                allowClear={false}
                placeholder={t(`${translationPrefix}.placeholders.selectDate`)}
                data-test-id={`input-session-date-${index}`}
              />
            </div>
            <div className={styles.dateTimeField}>
              <RequiredFieldLabel required className={styles.sessionFieldLabel}>
                {t(`${translationPrefix}.fields.startTime`)}
              </RequiredFieldLabel>
              <TimePicker
                format="HH:mm"
                className={classNames('w-100', styles.timePicker)}
                value={parseSessionTime(session.startTime) ?? undefined}
                onChange={(time) =>
                  onUpdate({
                    startTime: formatSessionTime(time),
                  })
                }
                allowClear={false}
                placeholder={t(`${translationPrefix}.placeholders.selectTime`)}
                data-test-id={`input-session-time-${index}`}
              />
            </div>
          </div>

          <div
            className={styles.durationRow}
            data-test-id={`input-session-duration-${index}`}
          >
            <RequiredFieldLabel className={styles.sessionFieldLabel}>
              {t(`${translationPrefix}.fields.duration`)}
            </RequiredFieldLabel>
            <TextInput
              name={`session-duration-${index}`}
              type="number"
              value={session.duration}
              label=""
              placeHolder={t(`${translationPrefix}.placeholders.writeHere`)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const parsed = Number(e.target.value)
                onUpdate({
                  duration: Number.isFinite(parsed) ? parsed : 0,
                })
              }}
              error=""
              classes={{ input: styles.sessionInput }}
            />
          </div>

          <div className={styles.fieldGroup}>
            <RequiredFieldLabel required className={styles.sessionFieldLabel}>
              {t(`${translationPrefix}.fields.meetingUrl`)}
            </RequiredFieldLabel>
            <TextInput
              name={`session-url-${index}`}
              value={session.meetingUrl}
              label=""
              placeHolder={t(`${translationPrefix}.placeholders.meetingUrl`)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                onUpdate({ meetingUrl: e.target.value })
              }
              error=""
              classes={{ input: styles.sessionInput }}
            />
            <FieldHint>
              {t(`${translationPrefix}.fields.meetingUrlHint`)}
            </FieldHint>
          </div>
        </div>
      )}
    </div>
  )
}
